import React, { useState } from "react";
import { CCol, CContainer, CRow } from "@coreui/react-pro";
import { Link } from "react-router-dom";
import { BRAND_COLOR, SITE } from "../../config/site";
import { useSeo } from "../../hooks/useSeo";

// Celular con la app en la portada. Los negocios son categorías de ejemplo, no comercios reales.
const HeroMockup: React.FC = () => (
  <div className="hero-stage" aria-hidden="true">
    <div className="hero-card card-1">
      <span className="hero-card-icon" style={{ backgroundColor: "#E8F8EE" }}>✅</span>
      <div>
        <div className="fw-bold">¡Pedido aceptado!</div>
        <small className="text-secondary">El negocio ya lo está preparando</small>
      </div>
    </div>
    <div className="hero-card card-2">
      <span className="hero-card-icon" style={{ backgroundColor: "#FFF0F2" }}>💵</span>
      <div>
        <div className="fw-bold">Pagas al recibir</div>
        <small className="text-secondary">Sin tarjetas</small>
      </div>
    </div>
    <div className="hero-card card-3">
      <span className="hero-card-icon" style={{ backgroundColor: "#F0F7FF" }}>🛍️</span>
      <div>
        <div className="fw-bold">Listo para recoger</div>
        <small className="text-secondary">Pasa sin hacer fila</small>
      </div>
    </div>

    <div className="hero-phone">
      <div className="hero-screen">
        <div className="hero-screen-header">
          <small className="opacity-75">Entregar en</small>
          <div className="fw-bold">📍 Mi casa ▾</div>
          <div className="hero-search">🔍 ¿Qué se te antoja hoy?</div>
        </div>
        <div className="hero-chips">
          <span>🍔 Comida</span>
          <span>🛒 Mercado</span>
          <span>💊 Droguería</span>
        </div>
        {[
          { icon: "🍔", bg: "#FFF0F2", name: "Hamburguesas", info: "Domicilio · Recoger" },
          { icon: "🛒", bg: "#F0F7FF", name: "Mercado del barrio", info: "Domicilio · Programado" },
          { icon: "🍦", bg: "#FFF9F0", name: "Heladería", info: "Recoger en tienda" },
        ].map((store) => (
          <div key={store.name} className="hero-store">
            <span className="hero-store-icon" style={{ backgroundColor: store.bg }}>{store.icon}</span>
            <div>
              <div className="fw-bold">{store.name}</div>
              <small className="text-secondary">{store.info}</small>
            </div>
          </div>
        ))}
        <div className="hero-order">
          <div className="d-flex justify-content-between">
            <span className="fw-bold">🛵 Tu pedido va en camino</span>
          </div>
          <div className="hero-progress">
            <span className="done"></span>
            <span className="done"></span>
            <span className="done"></span>
            <span></span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Botones para descargar la app; si no hay enlaces configurados invita a escribir por WhatsApp
export const AppButtons: React.FC<{ light?: boolean }> = ({ light }) => {
  const outline = light ? "btn-outline-light" : "btn-outline-dark";

  if (!SITE.androidUrl && !SITE.iosUrl) {
    return (
      <div className="d-flex flex-wrap gap-3">
        <a href={SITE.whatsappUrl} target="_blank" rel="noreferrer" className="btn btn-lg fw-bold px-4 text-white" style={{ backgroundColor: BRAND_COLOR }}>
          <i className="bi bi-whatsapp me-2"></i>Escríbenos por WhatsApp
        </a>
        <a href="#como-funciona" className={`btn btn-lg fw-semibold px-4 ${outline}`}>
          ¿Cómo funciona?
        </a>
      </div>
    );
  }

  return (
    <div className="d-flex flex-wrap gap-3">
      {SITE.androidUrl && (
        <a href={SITE.androidUrl} target="_blank" rel="noreferrer" className="btn btn-lg fw-bold px-4 text-white" style={{ backgroundColor: BRAND_COLOR }}>
          <i className="bi bi-google-play me-2"></i>Descargar en Google Play
        </a>
      )}
      {SITE.iosUrl && (
        <a href={SITE.iosUrl} target="_blank" rel="noreferrer" className={`btn btn-lg fw-bold px-4 ${outline}`}>
          <i className="bi bi-apple me-2"></i>Descargar en App Store
        </a>
      )}
    </div>
  );
};

const steps = [
  { icon: "📱", title: "Descarga la app y regístrate", text: "Crea tu cuenta y guarda la dirección donde quieres recibir." },
  { icon: "🏪", title: "Elige un negocio de Chiquinquirá", text: "Mira su menú o catálogo, los precios y arma tu pedido." },
  { icon: "🛵", title: "Recibe o recoge tu pedido", text: "Escoge cómo lo quieres y sigue su estado desde la app." },
  { icon: "💵", title: "Paga al recibir", text: "Pagas cuando tienes tu pedido en la mano. No te pedimos tarjeta." },
];

const deliveryTypes = [
  {
    icon: "🛵",
    title: "A domicilio",
    text: "Un domiciliario lleva tu pedido hasta la puerta de tu casa u oficina.",
  },
  {
    icon: "🛍️",
    title: "Recoger en tienda",
    text: "Pides desde donde estés y pasas por él cuando esté listo, sin hacer fila y sin costo de envío.",
  },
  {
    icon: "🗓️",
    title: "Programado",
    text: "Escoge el día y la hora en que quieres recibirlo. Ideal para el mercado de la semana o el almuerzo.",
  },
];

const reasons = [
  { icon: "🤝", title: "Compras a negocios de aquí", text: "Restaurantes, tiendas, droguerías y comercios de Chiquinquirá, con sus propios precios." },
  { icon: "🔔", title: "Sabes en qué va tu pedido", text: "Te avisamos cuando el negocio lo acepta, lo prepara y cuando va en camino." },
  { icon: "🏷️", title: "Promociones de tus negocios favoritos", text: "Combos, descuentos y anuncios que los negocios publican para ti." },
  { icon: "🔒", title: "Tus datos, cuidados", text: "No guardamos datos de tarjetas y puedes pedir que eliminemos tu cuenta cuando quieras." },
];

// Lo que se puede pedir en Chiquinquirá (texto para buscadores y personas; solo categorías que existen en la app)
const localCategories = [
  { icon: "🍔", title: "Comida y restaurantes", text: "Almuerzos, comidas rápidas, asaderos, pizzerías y lo que se te antoje, de los restaurantes de Chiquinquirá." },
  { icon: "🛒", title: "Mercado", text: "El mercado de la semana o lo que te faltó para la comida, de las tiendas y supermercados del barrio." },
  { icon: "💊", title: "Droguerías", text: "Lo que necesitas de la droguería, sin salir de casa cuando no te sientes bien." },
  { icon: "🍦", title: "Heladerías, panaderías y más", text: "Postres, onces y otros negocios de la ciudad que ya están en DomicilApp." },
];

const faqs = [
  {
    q: "¿DomicilApp hace domicilios en Chiquinquirá?",
    a: "Sí. DomicilApp nace en Chiquinquirá, Boyacá, y atiende pedidos a domicilio, para recoger en tienda y programados con restaurantes, tiendas, droguerías y otros negocios de la ciudad.",
  },
  {
    q: "¿Cuánto cuesta usar DomicilApp?",
    a: "Descargar la app y crear tu cuenta es gratis. Pagas los productos al precio del negocio y, si lo pides a domicilio, el valor del envío, que ves antes de confirmar el pedido.",
  },
  {
    q: "¿Cómo pago mi pedido?",
    a: "Por ahora los pagos son solo contra entrega: pagas al domiciliario cuando recibes tu pedido, o en el negocio cuando lo recoges.",
  },
  {
    q: "¿Puedo cancelar un pedido?",
    a: "Sí, mientras el negocio no lo haya empezado a preparar. Si ya está en preparación o en camino, la cancelación depende de que el negocio la acepte. Si tienes algún problema, escríbenos y te ayudamos.",
  },
  {
    q: "¿Por qué la app me pide la ubicación al confirmar?",
    a: "La usamos una sola vez para comprobar que el pedido es real y que la dirección de entrega tiene sentido. Así protegemos a los negocios y a los domiciliarios de pedidos falsos. Lo explicamos en la política de privacidad.",
  },
  {
    q: "Me llegó algo mal o incompleto, ¿qué hago?",
    a: "Escríbenos por WhatsApp o al correo de soporte con el número de tu pedido. Hablamos con el negocio y te damos una solución.",
  },
  {
    q: "¿Cómo elimino mi cuenta?",
    a: `Envía un correo desde el correo con el que te registraste a ${SITE.supportEmail} con el asunto "Eliminar mi cuenta". Los detalles están al final de la política de privacidad.`,
  },
];

export const Home: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  // Igual que en public/index.html (valores por defecto para quien no ejecuta JavaScript)
  useSeo({
    title: "Domicilios en Chiquinquirá: comida, restaurantes y mercado | DomicilApp",
    description: "Pide domicilios en Chiquinquirá desde el celular: comida, restaurantes, mercado y droguerías. Recíbelo en casa, recógelo sin filas o prográmalo. Pagas al recibir.",
  });

  // Datos estructurados de las preguntas frecuentes (schema.org FAQPage)
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: { "@type": "Answer", text: faq.a },
    })),
  };

  return (
    <div className="home-page bg-light">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* PORTADA */}
      <section className="hero-section py-5 text-white">
        <CContainer className="py-lg-4">
          <CRow className="align-items-center gy-5">
            <CCol lg={6}>
              <span className="d-inline-block small fw-bold text-uppercase mb-3" style={{ color: BRAND_COLOR, letterSpacing: ".08em" }}>
                {SITE.slogan}
              </span>
              <h1 className="display-5 fw-bold text-white mb-3 lh-sm">
                Domicilios en Chiquinquirá de los negocios que ya conoces, <span style={{ color: BRAND_COLOR }}>sin salir de casa</span>.
              </h1>
              <p className="fs-5 text-white-50 mb-4">
                Pide comida, mercado, droguería y más desde el celular. Te lo llevamos a tu casa, lo recoges sin filas o lo programas para cuando lo necesites. Y pagas al recibir.
              </p>
              <AppButtons light />
            </CCol>
            <CCol lg={6}>
              <HeroMockup />
            </CCol>
          </CRow>
        </CContainer>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como-funciona" className="py-5 bg-white">
        <CContainer className="py-lg-3">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark">Así de fácil es pedir</h2>
            <p className="text-secondary mb-0">Cuatro pasos y tu pedido está en camino.</p>
          </div>
          <CRow className="g-4">
            {steps.map((step, idx) => (
              <CCol key={idx} sm={6} lg={3}>
                <div className="h-100 p-4 rounded-4 border bg-light">
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="rounded-circle text-white fw-bold d-inline-flex align-items-center justify-content-center" style={{ width: 32, height: 32, backgroundColor: BRAND_COLOR }}>
                      {idx + 1}
                    </span>
                    <span className="fs-3">{step.icon}</span>
                  </div>
                  <h5 className="fw-bold text-dark">{step.title}</h5>
                  <p className="text-secondary mb-0">{step.text}</p>
                </div>
              </CCol>
            ))}
          </CRow>
        </CContainer>
      </section>

      {/* FORMAS DE ENTREGA */}
      <section id="entregas" className="py-5">
        <CContainer className="py-lg-3">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark">Tú decides cómo recibirlo</h2>
            <p className="text-secondary mb-0">Cada negocio indica en la app qué opciones tiene disponibles.</p>
          </div>
          <CRow className="g-4">
            {deliveryTypes.map((type, idx) => (
              <CCol key={idx} md={4}>
                <div className="h-100 p-4 rounded-4 bg-white shadow-sm text-center">
                  <div className="fs-1 mb-2">{type.icon}</div>
                  <h4 className="fw-bold text-dark">{type.title}</h4>
                  <p className="text-secondary mb-0">{type.text}</p>
                </div>
              </CCol>
            ))}
          </CRow>
        </CContainer>
      </section>

      {/* DOMICILIOS EN CHIQUINQUIRÁ */}
      <section id="chiquinquira" className="py-5 bg-white">
        <CContainer className="py-lg-3">
          <div className="text-center mb-5 mx-auto" style={{ maxWidth: "720px" }}>
            <h2 className="fw-bold text-dark">Domicilios en Chiquinquirá</h2>
            <p className="text-secondary mb-0">
              Pide comida a domicilio, haz el mercado o encarga lo de la droguería a los negocios de Chiquinquirá. Todo desde una sola app y pagando al recibir.
            </p>
          </div>
          <CRow className="g-4">
            {localCategories.map((category, idx) => (
              <CCol key={idx} sm={6} lg={3}>
                <div className="h-100 p-4 rounded-4 border bg-light">
                  <div className="fs-2 mb-2">{category.icon}</div>
                  <h3 className="h5 fw-bold text-dark">{category.title}</h3>
                  <p className="text-secondary mb-0">{category.text}</p>
                </div>
              </CCol>
            ))}
          </CRow>
        </CContainer>
      </section>

      {/* POR QUÉ DOMICILAPP */}
      <section className="py-5 bg-white">
        <CContainer className="py-lg-3">
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark">¿Por qué pedir con DomicilApp?</h2>
          </div>
          <CRow className="g-4">
            {reasons.map((reason, idx) => (
              <CCol key={idx} md={6}>
                <div className="d-flex gap-3">
                  <div className="fs-2">{reason.icon}</div>
                  <div>
                    <h5 className="fw-bold text-dark mb-1">{reason.title}</h5>
                    <p className="text-secondary mb-0">{reason.text}</p>
                  </div>
                </div>
              </CCol>
            ))}
          </CRow>
        </CContainer>
      </section>

      {/* PREGUNTAS FRECUENTES */}
      <section id="preguntas" className="py-5">
        <CContainer className="py-lg-3" style={{ maxWidth: "820px" }}>
          <div className="text-center mb-5">
            <h2 className="fw-bold text-dark">Preguntas frecuentes</h2>
            <p className="text-secondary mb-0">
              ¿No encuentras tu respuesta? <Link to="/contactus" className="fw-semibold text-decoration-none" style={{ color: BRAND_COLOR }}>Escríbenos</Link>.
            </p>
          </div>
          <div className="d-flex flex-column gap-2">
            {faqs.map((faq, idx) => {
              const open = openFaq === idx;
              return (
                <div key={idx} className="bg-white rounded-3 shadow-sm">
                  <button
                    type="button"
                    className="btn w-100 text-start d-flex justify-content-between align-items-center p-3 fw-semibold text-dark border-0"
                    aria-expanded={open}
                    aria-controls={`faq-${idx}`}
                    onClick={() => setOpenFaq(open ? null : idx)}
                  >
                    {faq.q}
                    <span className="fs-5 ms-3" style={{ color: BRAND_COLOR }}>{open ? "−" : "+"}</span>
                  </button>
                  <p id={`faq-${idx}`} className="px-3 pb-3 mb-0 text-secondary" hidden={!open}>{faq.a}</p>
                </div>
              );
            })}
          </div>
        </CContainer>
      </section>

      {/* AYUDA */}
      <section className="pt-5 bg-white">
        <CContainer style={{ maxWidth: "820px" }}>
          <div className="help-card p-4 p-md-5 rounded-4 d-flex flex-column flex-md-row align-items-md-center gap-4">
            <div className="help-card-icon flex-shrink-0">💬</div>
            <div className="flex-grow-1">
              <h3 className="fw-bold text-dark mb-2">Estamos para ayudarte</h3>
              <p className="text-secondary mb-3">
                DomicilApp nace en Chiquinquirá y detrás hay personas que leen cada mensaje. Si algo no salió bien con tu pedido, cuéntanos.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <a href={SITE.whatsappUrl} target="_blank" rel="noreferrer" className="btn btn-whatsapp fw-bold px-4">
                  <i className="bi bi-whatsapp me-2"></i>WhatsApp
                </a>
                <a href={`mailto:${SITE.supportEmail}`} className="btn btn-outline-dark fw-semibold px-4">
                  <i className="bi bi-envelope me-2"></i>{SITE.supportEmail}
                </a>
              </div>
            </div>
          </div>
        </CContainer>
      </section>

      {/* PARA NEGOCIOS (secundario) */}
      <section id="negocios" className="py-5 bg-white">
        <CContainer style={{ maxWidth: "820px" }}>
          <div className="p-4 p-md-5 rounded-4 border d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-4">
            <div>
              <h4 className="fw-bold text-dark mb-2">¿Tienes un negocio?</h4>
              <p className="text-secondary mb-0">
                Recibe pedidos de tus clientes y administra tu catálogo y tus horarios desde el portal de DomicilApp.
              </p>
            </div>
            {SITE.portalUrl && (
              <a href={`${SITE.portalUrl}/signup`} className="btn btn-dark fw-bold px-4 text-nowrap">
                Registrar mi negocio
              </a>
            )}
          </div>
        </CContainer>
      </section>

    </div>
  );
};
